import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine
} from 'recharts';

interface FilterProps {
  dateRange: {
    start: string;
    end: string;
    preset: string;
  };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface CashFlowAnalysisProps {
  filters: FilterProps;
}

const CashFlowAnalysis: React.FC<CashFlowAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedView, setSelectedView] = useState('net');

  // Cash flow summary metrics
  const cashFlowSummary = [
    {
      title: 'صافي التدفق النقدي',
      value: '1,156,800',
      change: '+15.2%',
      trend: 'up',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600',
      status: 'positive'
    },
    {
      title: 'التدفق النقدي التشغيلي',
      value: '1,423,600',
      change: '+12.8%',
      trend: 'up',
      icon: BanknotesIcon,
      color: 'from-blue-500 to-blue-600',
      status: 'positive'
    },
    {
      title: 'التدفق النقدي الاستثماري',
      value: '-186,400',
      change: '-8.3%',
      trend: 'down',
      icon: ChartBarIcon,
      color: 'from-orange-500 to-orange-600',
      status: 'negative'
    },
    {
      title: 'التدفق النقدي التمويلي',
      value: '-80,400',
      change: '+22.1%',
      trend: 'up',
      icon: CreditCardIcon,
      color: 'from-purple-500 to-purple-600',
      status: 'negative'
    }
  ];

  // Monthly cash flow data
  const monthlyCashFlowData = [
    {
      month: 'يناير',
      operating: 1200000,
      investing: -150000,
      financing: -50000,
      net: 1000000,
      cumulative: 1000000,
      inflow: 2100000,
      outflow: 1100000
    },
    {
      month: 'فبراير',
      operating: 1250000,
      investing: -180000,
      financing: -70000,
      net: 1000000,
      cumulative: 2000000,
      inflow: 2300000,
      outflow: 1300000
    },
    {
      month: 'مارس',
      operating: 1300000,
      investing: -200000,
      financing: -100000,
      net: 1000000,
      cumulative: 3000000,
      inflow: 2500000,
      outflow: 1500000
    },
    {
      month: 'أبريل',
      operating: 1280000,
      investing: -160000,
      financing: -80000,
      net: 1040000,
      cumulative: 4040000,
      inflow: 2400000,
      outflow: 1360000
    },
    {
      month: 'مايو',
      operating: 1350000,
      investing: -170000,
      financing: -90000,
      net: 1090000,
      cumulative: 5130000,
      inflow: 2600000,
      outflow: 1510000
    },
    {
      month: 'يونيو',
      operating: 1423600,
      investing: -186400,
      financing: -80400,
      net: 1156800,
      cumulative: 6286800,
      inflow: 2847500,
      outflow: 1690700
    }
  ];

  // Cash flow forecasting
  const forecastData = [
    { month: 'يوليو', forecast: 1200000, confidence: 85 },
    { month: 'أغسطس', forecast: 1350000, confidence: 80 },
    { month: 'سبتمبر', forecast: 1180000, confidence: 75 },
    { month: 'أكتوبر', forecast: 1420000, confidence: 70 },
    { month: 'نوفمبر', forecast: 1380000, confidence: 65 },
    { month: 'ديسمبر', forecast: 1500000, confidence: 60 }
  ];

  // Cash flow by category
  const cashFlowCategories = [
    {
      category: 'المبيعات النقدية',
      type: 'inflow',
      amount: 1423750,
      percentage: 50,
      trend: '+12%',
      color: '#10B981'
    },
    {
      category: 'تحصيل الذمم',
      type: 'inflow',
      amount: 853125,
      percentage: 30,
      trend: '+8%',
      color: '#3B82F6'
    },
    {
      category: 'إيرادات أخرى',
      type: 'inflow',
      amount: 570625,
      percentage: 20,
      trend: '+15%',
      color: '#8B5CF6'
    },
    {
      category: 'رواتب وأجور',
      type: 'outflow',
      amount: 865440,
      percentage: 45,
      trend: '+5%',
      color: '#EF4444'
    },
    {
      category: 'مصروفات تشغيلية',
      type: 'outflow',
      amount: 480800,
      percentage: 25,
      trend: '+8%',
      color: '#F59E0B'
    },
    {
      category: 'مدفوعات أخرى',
      type: 'outflow',
      amount: 344460,
      percentage: 18,
      trend: '+3%',
      color: '#6B7280'
    }
  ];

  // Working capital analysis
  const workingCapitalData = [
    { month: 'يناير', current_assets: 3500000, current_liabilities: 2100000, working_capital: 1400000 },
    { month: 'فبراير', current_assets: 3650000, current_liabilities: 2150000, working_capital: 1500000 },
    { month: 'مارس', current_assets: 3800000, current_liabilities: 2200000, working_capital: 1600000 },
    { month: 'أبريل', current_assets: 3750000, current_liabilities: 2180000, working_capital: 1570000 },
    { month: 'مايو', current_assets: 3900000, current_liabilities: 2250000, working_capital: 1650000 },
    { month: 'يونيو', current_assets: 4050000, current_liabilities: 2300000, working_capital: 1750000 }
  ];

  // Cash flow ratios
  const cashFlowRatios = [
    {
      ratio: 'نسبة التدفق النقدي التشغيلي',
      value: '1.74',
      benchmark: '> 1.2',
      status: 'excellent',
      description: 'قدرة ممتازة على توليد النقد من العمليات'
    },
    {
      ratio: 'نسبة تغطية الديون',
      value: '2.8',
      benchmark: '> 1.5',
      status: 'excellent',
      description: 'قدرة قوية على سداد الديون'
    },
    {
      ratio: 'دورة التحويل النقدي',
      value: '45 يوم',
      benchmark: '< 60 يوم',
      status: 'good',
      description: 'كفاءة جيدة في إدارة رأس المال العامل'
    },
    {
      ratio: 'نسبة الاحتياطي النقدي',
      value: '3.2 شهر',
      benchmark: '> 3 شهر',
      status: 'good',
      description: 'احتياطي نقدي كافي لتغطية المصروفات'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'warning':
      case 'critical':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <CheckCircleIcon className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Cash Flow Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cashFlowSummary.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                    <div className={`w-10 h-10 bg-gradient-to-r ${metric.color} rounded-lg p-2`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {metric.title}
                  </h3>
                  
                  <div className="flex items-baseline space-x-2 rtl:space-x-reverse mb-2">
                    <span className={`text-2xl font-bold ${
                      metric.status === 'positive' 
                        ? 'text-green-600 dark:text-green-400'
                        : metric.status === 'negative'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {metric.value.toLocaleString()}
                    </span>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      ر.س
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      metric.trend === 'up' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {metric.trend === 'up' ? (
                        <ArrowTrendingUpIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                      )}
                      {metric.change}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Cash Flow Trend Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Cash Flow Components */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              مكونات التدفق النقدي الشهري
            </h3>
            <select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="net">صافي التدفق</option>
              <option value="components">المكونات</option>
              <option value="cumulative">التراكمي</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {selectedView === 'components' ? (
              <BarChart data={monthlyCashFlowData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
                />
                <Tooltip
                  formatter={(value: any, name: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, name]}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="operating" fill="#10B981" name="التشغيلي" radius={[4, 4, 0, 0]} />
                <Bar dataKey="investing" fill="#F59E0B" name="الاستثماري" radius={[4, 4, 0, 0]} />
                <Bar dataKey="financing" fill="#8B5CF6" name="التمويلي" radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : selectedView === 'cumulative' ? (
              <AreaChart data={monthlyCashFlowData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
                />
                <Tooltip
                  formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'التدفق التراكمي']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.6}
                />
              </AreaChart>
            ) : (
              <LineChart data={monthlyCashFlowData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
                />
                <Tooltip
                  formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'صافي التدفق']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="net"
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </motion.div>

        {/* Cash Flow Forecast */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            توقعات التدفق النقدي
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="forecast"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <YAxis 
                yAxisId="confidence"
                orientation="right"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  if (name === 'مستوى الثقة') return [`${value}%`, name];
                  return [`${(value / 1000).toLocaleString()} ألف ر.س`, name];
                }}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar 
                yAxisId="forecast" 
                dataKey="forecast" 
                fill="#3B82F6" 
                fillOpacity={0.6}
                name="التوقع" 
                radius={[4, 4, 0, 0]} 
              />
              <Line
                yAxisId="confidence"
                type="monotone"
                dataKey="confidence"
                stroke="#10B981"
                strokeWidth={3}
                name="مستوى الثقة"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Cash Flow Categories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          تفصيل التدفقات النقدية
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cash Inflows */}
          <div>
            <h4 className="text-md font-semibold text-green-600 dark:text-green-400 mb-3 flex items-center">
              <ArrowRightIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
              التدفقات الداخلة
            </h4>
            <div className="space-y-3">
              {cashFlowCategories.filter(cat => cat.type === 'inflow').map((category, index) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
                >
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {category.category}
                    </span>
                  </div>
                  <div className="text-right rtl:text-left">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {(category.amount / 1000).toLocaleString()} ألف ر.س
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      {category.trend}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Cash Outflows */}
          <div>
            <h4 className="text-md font-semibold text-red-600 dark:text-red-400 mb-3 flex items-center">
              <ArrowLeftIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
              التدفقات الخارجة
            </h4>
            <div className="space-y-3">
              {cashFlowCategories.filter(cat => cat.type === 'outflow').map((category, index) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg"
                >
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {category.category}
                    </span>
                  </div>
                  <div className="text-right rtl:text-left">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {(category.amount / 1000).toLocaleString()} ألف ر.س
                    </div>
                    <div className="text-xs text-red-600 dark:text-red-400">
                      {category.trend}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Working Capital and Ratios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Working Capital Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            رأس المال العامل
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={workingCapitalData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, name]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="current_assets" fill="#10B981" name="الأصول المتداولة" radius={[4, 4, 0, 0]} />
              <Bar dataKey="current_liabilities" fill="#EF4444" name="الخصوم المتداولة" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="working_capital"
                stroke="#3B82F6"
                strokeWidth={3}
                name="رأس المال العامل"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Cash Flow Ratios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            نسب التدفق النقدي
          </h3>
          <div className="space-y-4">
            {cashFlowRatios.map((ratio, index) => (
              <motion.div
                key={ratio.ratio}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {ratio.ratio}
                  </h4>
                  <div className={`flex items-center px-2 py-1 rounded-full ${getStatusColor(ratio.status)}`}>
                    {getStatusIcon(ratio.status)}
                    <span className="text-xs font-medium mr-1 rtl:mr-0 rtl:ml-1">
                      {ratio.status === 'excellent' ? 'ممتاز' : ratio.status === 'good' ? 'جيد' : 'تحذير'}
                    </span>
                  </div>
                </div>
                <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {ratio.value}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  المعيار: {ratio.benchmark}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  {ratio.description}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CashFlowAnalysis;
